// Time slot utility functions for calendar selection

/**
 * Convert 24-hour format time to 12-hour format
 * @param time - Time in "HH:MM" format (already in location timezone from API)
 * @param timezone - Timezone string (kept for API compatibility but not used for conversion)
 * @returns Time in "h:mm am/pm" format
 */
export const formatTime = (time: string, _timezone?: string): string => {
  const [hours, minutes] = time.split(':');
  const hour = Number.parseInt(hours, 10);

  // Simple conversion - API times are already in location timezone
  const ampm = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

/**
 * Calculate duration between two times in minutes
 * @param startTime - Start time in "HH:MM" format
 * @param endTime - End time in "HH:MM" format
 * @returns Duration in minutes
 */
export const calculateDuration = (startTime: string, endTime: string): number => {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);
  
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  
  return endTotalMinutes - startTotalMinutes;
};

/**
 * Check if slots between two indices are consecutive and available
 * @param availableSlots - Array of available slots from API
 * @param firstIndex - Index of first slot
 * @param lastIndex - Index of last slot
 * @returns Object with availability status and slots between
 */
export const checkConsecutiveAvailability = (
  availableSlots: Array<{ startTime: string; endTime: string; available: boolean }>,
  firstIndex: number,
  lastIndex: number
) => {
  const slotsBetween = [];
  let allAvailable = true;
  
  // Check all slots between first and last
  for (let i = Math.min(firstIndex, lastIndex); i <= Math.max(firstIndex, lastIndex); i++) {
    const betweenSlot = availableSlots[i];
    const betweenTimeString = `${betweenSlot.startTime} - ${betweenSlot.endTime}`;
    
    if (betweenSlot.available === false) {
      allAvailable = false;
      break; // Chain broken
    }
    slotsBetween.push(betweenTimeString);
  }
  
  return { allAvailable, slotsBetween };
};

/**
 * Process multiple time selections to find consecutive chains
 * @param times - Array of selected time strings
 * @returns Array of consecutive time slots
 */
export const processConsecutiveSelection = (times: string[]): string[] => {
  if (times.length === 0) return [];
  
  // Sort times using localeCompare for reliable alphabetical sorting
  const sortedTimes = [...times].sort((a, b) => a.localeCompare(b));
  const consecutiveSlots = [];
  
  for (let i = 0; i < sortedTimes.length; i++) {
    const currentTime = sortedTimes[i];
    consecutiveSlots.push(currentTime);
    
    // Check if next slot exists and is consecutive
    if (i < sortedTimes.length - 1) {
      const nextTime = sortedTimes[i + 1];
      const currentEnd = currentTime.split(' - ')[1];
      const nextStart = nextTime.split(' - ')[0];
      
      // If not consecutive, break the chain
      if (currentEnd !== nextStart) {
        // Start new chain from next slot
        consecutiveSlots.length = 0; // Clear current chain
      }
    }
  }
  
  // Get final slots to use (last consecutive chain)
  return consecutiveSlots.length > 0 ? consecutiveSlots : [sortedTimes.at(-1) || ''];
};

/**
 * Create time range string from first and last slots
 * @param slots - Array of time slot strings
 * @returns Time range string (e.g., "6:30 pm - 8:00 pm")
 */
export const createTimeRange = (slots: string[]): string => {
  if (slots.length === 0) return '';
  
  const firstTime = slots[0];
  const lastTime = slots.at(-1) || slots[0];
  
  if (!firstTime || !lastTime) return '';
  
  const firstStartTime = firstTime.split(' - ')[0];
  const lastEndTime = lastTime.split(' - ')[1];
  
  return `${firstStartTime} - ${lastEndTime}`;
};
